import { Link } from '@tanstack/react-router';
import { CheckIcon, PiggyBankIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Panel } from '../common/Panel';
import { type InboxItem, useInboxActions } from '../inbox/useInboxActions';
import { SavingsPickerSheet } from '../savings/SavingsPickerSheet';
import { CategoryPickerSheet } from '../transactions/CategoryPickerSheet';
import { shortDayLabel } from './dates';
import { formatAmount } from './PurchaseRow';

const MAX_ROWS = 5;

/**
 * New purchases waiting for a category, on top of Köp. One tap accepts the
 * suggestion; "Annan kategori" opens the full picker and "Sparande" the savings
 * pots. Renders nothing when empty.
 */
export function InboxStrip() {
  const { items, withSuggestion, bulkRunning, categorize, asSavings, acceptAll } =
    useInboxActions();
  const [pickerFor, setPickerFor] = useState<InboxItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingsFor, setSavingsFor] = useState<InboxItem | null>(null);
  const [savingsOpen, setSavingsOpen] = useState(false);

  if (items.length === 0 && !pickerOpen && !savingsOpen) return null;

  const visible = items.slice(0, MAX_ROWS);
  const count = items.length;

  return (
    <>
      {count > 0 && (
        <Panel
          title={
            <span className="flex items-center gap-2">
              Att sortera
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-warning-soft px-1.5 text-xs font-semibold text-warning tabular-nums">
                {count}
              </span>
            </span>
          }
          description="Nya köp som behöver en kategori."
          action={
            <Link to="/inbox" className="font-medium text-primary hover:underline">
              Visa alla
            </Link>
          }
        >
          <ul className="-mx-4 divide-y divide-border sm:-mx-5">
            {visible.map((item) => (
              <li key={item.id} className="px-4 py-3 sm:px-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[15px] leading-6 font-medium">{item.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {shortDayLabel(item.date)}
                      {item.accountName ? ` · ${item.accountName}` : ''}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-[15px] leading-6 font-medium whitespace-nowrap tabular-nums',
                      item.amount > 0 ? 'text-positive' : 'text-foreground',
                    )}
                  >
                    {formatAmount(item.amount)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {item.suggestion && (
                    <button
                      type="button"
                      onClick={() => {
                        if (item.suggestion) {
                          void categorize(item, item.suggestion.categoryId, false);
                        }
                      }}
                      disabled={bulkRunning}
                      aria-label={`Sortera som ${item.suggestion.categoryName}`}
                      className="inline-flex h-8 max-w-full min-w-0 items-center gap-1.5 rounded-full bg-primary/10 px-3 text-sm font-medium text-primary outline-none transition-colors hover:bg-primary/15 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 max-sm:h-10"
                    >
                      <CheckIcon aria-hidden className="size-3.5 shrink-0" />
                      <span className="truncate">{item.suggestion.categoryName}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setPickerFor(item);
                      setPickerOpen(true);
                    }}
                    disabled={bulkRunning}
                    className="inline-flex h-8 items-center rounded-full border border-border px-3 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 max-sm:h-10"
                  >
                    {item.suggestion ? 'Annan kategori' : 'Välj kategori'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSavingsFor(item);
                      setSavingsOpen(true);
                    }}
                    disabled={bulkRunning}
                    aria-label="Markera som sparande"
                    className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border px-3 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 max-sm:h-10"
                  >
                    <PiggyBankIcon aria-hidden className="size-3.5 shrink-0" />
                    Sparande
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {(withSuggestion.length > 1 || count > MAX_ROWS) && (
            <div className="-mx-4 -mb-4 flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 sm:-mx-5 sm:-mb-5 sm:px-5">
              {withSuggestion.length > 1 ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void acceptAll()}
                  disabled={bulkRunning}
                >
                  <CheckIcon aria-hidden />
                  Godkänn alla förslag ({withSuggestion.length})
                </Button>
              ) : (
                <span />
              )}
              {count > MAX_ROWS && (
                <Link
                  to="/inbox"
                  className="inline-flex min-h-10 items-center px-1 text-sm text-muted-foreground hover:text-foreground sm:min-h-0"
                >
                  {count - MAX_ROWS} till
                </Link>
              )}
            </div>
          )}
        </Panel>
      )}

      <SavingsPickerSheet
        open={savingsOpen}
        onOpenChange={setSavingsOpen}
        title={savingsFor?.description}
        onSelect={(pot) => {
          if (savingsFor) void asSavings(savingsFor, pot);
        }}
      />

      <CategoryPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={pickerFor?.description ?? 'Välj kategori'}
        value={pickerFor?.suggestion?.categoryId ?? null}
        suggestions={
          pickerFor?.suggestion
            ? [
                {
                  categoryId: pickerFor.suggestion.categoryId,
                  label: pickerFor.suggestion.categoryName,
                },
              ]
            : []
        }
        // Empty pattern = no rule offered (incoming Swish is sorted by hand)
        showCreateRule={(pickerFor?.rulePattern.length ?? 0) > 0}
        onSelect={(categoryId, { createRule }) => {
          if (pickerFor) void categorize(pickerFor, categoryId, createRule);
        }}
      />
    </>
  );
}
